import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { notificationService } from '../services/notificationService';

interface Notification {
  id: string;
  type: 'message' | 'request_update' | 'system';
  title: string;
  message: string;
  read: boolean;
  created_at: string;
  data?: any;
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  sendNotification: (notification: Omit<Notification, 'id' | 'created_at' | 'read'>) => void;
  requestNotificationPermission: () => Promise<boolean>;
  showBrowserNotification: (title: string, options?: NotificationOptions) => void;
  testNotification: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission>('default');

  // Check browser notification permission
  useEffect(() => {
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
    }
  }, []);

  // Fetch existing notifications
  useEffect(() => {
    if (user) {
      fetchNotifications();
    }
  }, [user]);

  // Set up real-time subscription for new messages
  useEffect(() => {
    if (!user) return;

    // Subscribe to new messages
    const messageSubscription = supabase
      .channel('request_messages')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'request_message',
        },
        async (payload) => {
          const newMessage = payload.new;
          
          console.log('🔔 New message received:', newMessage);
          console.log('🔔 Current user ID:', user.id);
          console.log('🔔 Message sender ID:', newMessage.sender_id);
          
          // Don't notify if the message is from the current user
          if (newMessage.sender_id === user.id) {
            console.log('🔔 Message from current user, skipping notification');
            return;
          }

          // Get request details for the notification
          const { data: requestData } = await supabase
            .from('service_requests')
            .select('id, type, user_id, brands(name)')
            .eq('id', newMessage.request_id)
            .single();

          console.log('🔔 Request data for notification:', requestData);

          if (requestData) {
            // Check if current user is a team member
            const { data: userRoleData } = await supabase
              .from('users')
              .select('role')
              .eq('id', user.id)
              .single();

            const isTeamMember = userRoleData?.role !== null;
            console.log('🔔 Current user role:', userRoleData?.role, 'Is team member:', isTeamMember);

            // Team members get notified for all messages they didn't send
            // Regular users get notified for messages about their own requests
            if (isTeamMember || requestData.user_id === user.id) {
              console.log('🔔 Creating notification for user:', user.id);
              
              const brandName = (requestData.brands as any)?.name || 'Unknown Brand';
              const requestType = requestData.type?.replace(/-/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()) || 'Request';
              
              // Determine sender name for the notification
              let senderName = 'Unknown';
              if (newMessage.sender === 'user') {
                senderName = 'Client';
              } else if (newMessage.sender === 'admin') {
                // Get the admin user's name
                const { data: senderData } = await supabase
                  .from('users')
                  .select('full_name, email')
                  .eq('id', newMessage.sender_id)
                  .single();
                senderName = senderData?.full_name || senderData?.email?.split('@')[0] || 'Team Member';
              }

              // Create notification
              const notification: Omit<Notification, 'id' | 'created_at' | 'read'> = {
                type: 'message',
                title: `New Message from ${senderName}`,
                message: `New message regarding ${brandName} ${requestType.toLowerCase()} request.`,
                data: {
                  messageId: newMessage.id,
                  requestId: newMessage.request_id,
                  senderId: newMessage.sender_id,
                  message: newMessage.message,
                  requestType: requestType,
                  brandName: brandName,
                  senderName: senderName
                }
              };

              console.log('🔔 Notification object created:', notification);

              // Add to local state
              addNotification(notification);

              // Show browser notification
              if (notificationPermission === 'granted') {
                console.log('🔔 Showing browser notification');
                showBrowserNotification(notification.title, {
                  body: notification.message,
                  icon: '/favicon.ico',
                  badge: '/favicon.ico',
                  tag: `message-${newMessage.id}`,
                  data: notification.data
                });
              } else {
                console.log('🔔 Browser notifications not granted, permission:', notificationPermission);
              }

              // Send email notification (if enabled)
              sendEmailNotification(notification);
            } else {
              console.log('🔔 User not eligible for notification. Is team member:', isTeamMember, 'Request owner:', requestData.user_id === user.id);
            }
          } else {
            console.log('🔔 No request data found for message');
          }
        }
      )
      .subscribe();

    return () => {
      messageSubscription.unsubscribe();
    };
  }, [user, notificationPermission]);

  const fetchNotifications = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('user_notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (!error && data) {
        setNotifications(data);
        setUnreadCount(data.filter(n => !n.read).length);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const addNotification = async (notification: Omit<Notification, 'id' | 'created_at' | 'read'>) => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('user_notifications')
        .insert([{
          user_id: user.id,
          type: notification.type,
          title: notification.title,
          message: notification.message,
          data: notification.data,
          read: false
        }])
        .select()
        .single();

      if (!error && data) {
        setNotifications(prev => [data, ...prev]);
        setUnreadCount(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error adding notification:', error);
    }
  };

  const markAsRead = async (id: string) => {
    try {
      const { error } = await supabase
        .from('user_notifications')
        .update({ read: true })
        .eq('id', id);

      if (!error) {
        setNotifications(prev => 
          prev.map(n => n.id === id ? { ...n, read: true } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('user_notifications')
        .update({ read: true })
        .eq('user_id', user.id)
        .eq('read', false);

      if (!error) {
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const sendNotification = async (notification: Omit<Notification, 'id' | 'created_at' | 'read'>) => {
    await addNotification(notification);
  };

  const requestNotificationPermission = async (): Promise<boolean> => {
    if (!('Notification' in window)) {
      console.log('This browser does not support notifications');
      return false;
    }

    if (Notification.permission === 'granted') {
      setNotificationPermission('granted');
      return true;
    }

    if (Notification.permission === 'denied') {
      setNotificationPermission('denied');
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      return permission === 'granted';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  };

  const showBrowserNotification = (title: string, options?: NotificationOptions) => {
    if (notificationPermission === 'granted') {
      new Notification(title, options);
    }
  };

  const testNotification = () => {
    console.log('🧪 Testing notification system...');
    
    // Create a test notification
    const testNotification: Omit<Notification, 'id' | 'created_at' | 'read'> = {
      type: 'system',
      title: 'Test Notification',
      message: 'This is a test notification to verify the system is working.',
      data: { test: true }
    };

    // Add to local state
    addNotification(testNotification);

    // Show browser notification
    if (notificationPermission === 'granted') {
      showBrowserNotification('Test Notification', {
        body: 'This is a test notification to verify the system is working.',
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag: 'test-notification'
      });
    }

    console.log('🧪 Test notification sent!');
  };

  const sendEmailNotification = async (notification: Omit<Notification, 'id' | 'created_at' | 'read'>) => {
    if (!user?.email) return;

    try {
      // Generate email content
      const htmlContent = notificationService.generateMessageEmailTemplate(
        notification.title.replace('New Message from ', ''),
        notification.data?.requestType || 'Request',
        notification.data?.message || notification.message,
        notification.data?.requestId || ''
      );

      const textContent = notificationService.generateMessageEmailText(
        notification.title.replace('New Message from ', ''),
        notification.data?.requestType || 'Request',
        notification.data?.message || notification.message,
        notification.data?.requestId || ''
      );

      // Send email notification
      await notificationService.sendEmail({
        to: user.email,
        subject: notification.title,
        html: htmlContent,
        text: textContent,
      });

      console.log('Email notification sent successfully');
    } catch (error) {
      console.error('Error sending email notification:', error);
    }
  };

  const value: NotificationContextType = {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    sendNotification,
    requestNotificationPermission,
    showBrowserNotification,
    testNotification
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}; 